<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';
import { isPhoneNumberValid } from 'shared/helpers/Validators';

const BRAZIL_DIAL_CODE = '+55';

const splitContactName = value => {
  const parts = (value || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'delete', 'update:show'],
  data() {
    return {
      countries,
      firstName: '',
      lastName: '',
      dialCode: BRAZIL_DIAL_CODE,
      phoneNumber: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    drawerStyle() {
      return {
        width: 'min(100%, 990px)',
      };
    },
    selectedCountryLabel() {
      const country = this.countries.find(
        item => item.dial_code === this.dialCode
      );
      if (!country) return this.dialCode;

      return `${country.id} ${country.dial_code}`;
    },
    editContactTitle() {
      return this.$t('EDIT_CONTACT.TITLE');
    },
    backLabel() {
      return 'Voltar';
    },
    firstNameLabel() {
      return 'Nome';
    },
    lastNameLabel() {
      return 'Sobrenome';
    },
    countryLabel() {
      return 'País';
    },
    phoneNumberLabel() {
      return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL');
    },
    whatsappPhoneHelper() {
      return 'Esse número de telefone já está no WhatsApp.';
    },
    normalizedPhoneNumber() {
      const digits = `${this.phoneNumber || ''}`.replace(/\D/g, '');
      return digits ? `${this.dialCode}${digits}` : '';
    },
    // Contato de grupo do WhatsApp: `phone_number` às vezes chega preenchido
    // com o JID numérico do grupo (18+ dígitos), que nunca passa no regex de
    // telefone (máx. 14) — sem isso, o drawer trava o Enviar pra sempre,
    // mesmo com o Nome preenchido corretamente.
    isGroupContact() {
      return `${this.contact.identifier || ''}`.endsWith('@g.us');
    },
    isPhoneNumberInvalid() {
      if (this.isGroupContact) return false;
      if (!this.normalizedPhoneNumber) return false;
      return !isPhoneNumberValid(this.normalizedPhoneNumber, this.dialCode);
    },
    canSubmit() {
      return !!this.firstName.trim() && !this.isPhoneNumberInvalid;
    },
  },
  watch: {
    // Observar o objeto inteiro (não só o id) reseta o formulário a cada
    // atualização do contato vinda da store (nova mensagem, leitura, etc.)
    // enquanto o drawer está aberto — apaga o que o usuário está digitando
    // e prende o Enviar (firstName volta vazio). Só o id importa aqui: é
    // a única mudança que exige recarregar os campos do zero.
    'contact.id': {
      handler() {
        this.setContactObject();
      },
      immediate: true,
    },
  },
  methods: {
    setContactObject() {
      const { firstName, lastName } = splitContactName(this.contact.name);
      const parsedPhone = parsePhoneNumber(this.contact.phone_number || '');

      this.firstName = firstName;
      this.lastName = lastName;

      if (parsedPhone?.countryCallingCode) {
        this.dialCode = `+${parsedPhone.countryCallingCode}`;
        this.phoneNumber = parsedPhone.nationalNumber || '';
      } else {
        this.dialCode = BRAZIL_DIAL_CODE;
        this.phoneNumber = (this.contact.phone_number || '').replace(/\D/g, '');
      }
    },
    onCancel() {
      this.$emit('cancel');
    },
    onBack() {
      // Fecha somente o drawer de edição; o painel de dados do contato fica aberto.
      this.$emit('cancel');
    },
    onDelete() {
      this.$emit('delete');
    },
    async onSubmit() {
      if (!this.canSubmit) return;

      const name = [this.firstName, this.lastName]
        .map(value => value.trim())
        .filter(Boolean)
        .join(' ');

      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          name,
          phone_number: this.normalizedPhoneNumber,
          additional_attributes: {
            ...this.contact.additional_attributes,
            country_code:
              this.countries.find(item => item.dial_code === this.dialCode)
                ?.id || '',
          },
        });
        await this.$store.dispatch('contacts/show', { id: this.contact.id });
        await this.$store.dispatch(
          'contacts/fetchContactableInbox',
          this.contact.id
        );
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        this.onCancel();
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const invalidAttrs = Array.isArray(error.data) ? error.data : [];
          if (invalidAttrs.includes('phone_number')) {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          } else {
            useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="contact-edit-drawer">
      <aside
        v-if="localShow"
        class="contact-edit-drawer"
        :style="drawerStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="editContactTitle"
        @click.stop
      >
        <header class="contact-edit-drawer__header">
          <button
            v-tooltip.top="backLabel"
            type="button"
            class="contact-edit-drawer__icon-button"
            :aria-label="backLabel"
            @click.stop="onBack"
          >
            <span class="i-lucide-arrow-left" />
          </button>
          <h2 class="contact-edit-drawer__title">{{ editContactTitle }}</h2>
          <button
            v-if="canDelete"
            v-tooltip.top="$t('DELETE_CONTACT.BUTTON_LABEL')"
            type="button"
            class="contact-edit-drawer__icon-button contact-edit-drawer__icon-button--delete"
            :disabled="uiFlags.isDeleting"
            :aria-label="$t('DELETE_CONTACT.BUTTON_LABEL')"
            @click="onDelete"
          >
            <span class="i-lucide-trash-2" />
          </button>
          <span v-else class="contact-edit-drawer__header-spacer" />
        </header>

        <form class="contact-edit-drawer__form" @submit.prevent="onSubmit">
          <div class="contact-edit-drawer__field-row">
            <span class="i-lucide-user contact-edit-drawer__field-icon" />
            <label class="contact-edit-drawer__field">
              <span class="contact-edit-drawer__label">
                {{ firstNameLabel }}
              </span>
              <input
                v-model="firstName"
                class="contact-edit-drawer__input"
                type="text"
                autocomplete="given-name"
              />
            </label>
          </div>

          <div
            class="contact-edit-drawer__field-row contact-edit-drawer__field-row--no-icon"
          >
            <label class="contact-edit-drawer__field">
              <span class="contact-edit-drawer__label">
                {{ lastNameLabel }}
              </span>
              <input
                v-model="lastName"
                class="contact-edit-drawer__input"
                type="text"
                autocomplete="family-name"
              />
            </label>
          </div>

          <div class="contact-edit-drawer__phone-row">
            <span class="i-lucide-phone contact-edit-drawer__field-icon" />
            <label
              class="contact-edit-drawer__field contact-edit-drawer__field--country"
            >
              <span class="contact-edit-drawer__label">
                {{ countryLabel }}
              </span>
              <select
                v-model="dialCode"
                class="contact-edit-drawer__input contact-edit-drawer__select"
                :aria-label="selectedCountryLabel"
              >
                <option
                  v-for="country in countries"
                  :key="country.id"
                  :value="country.dial_code"
                >
                  {{ country.id }} {{ country.dial_code }}
                </option>
              </select>
            </label>
            <label class="contact-edit-drawer__field">
              <span class="contact-edit-drawer__label">
                {{ phoneNumberLabel }}
              </span>
              <input
                v-model="phoneNumber"
                class="contact-edit-drawer__input"
                type="tel"
                autocomplete="tel-national"
              />
            </label>
            <span
              v-if="normalizedPhoneNumber && !isPhoneNumberInvalid"
              class="i-lucide-check contact-edit-drawer__phone-valid"
            />
          </div>

          <p
            v-if="isPhoneNumberInvalid"
            class="contact-edit-drawer__phone-helper contact-edit-drawer__phone-helper--error"
          >
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR') }}
          </p>
          <p v-else class="contact-edit-drawer__phone-helper">
            {{ whatsappPhoneHelper }}
          </p>

          <button
            v-tooltip="$t('CONTACT_FORM.FORM.SUBMIT')"
            type="submit"
            class="contact-edit-drawer__submit"
            :disabled="uiFlags.isUpdating || !canSubmit"
            :aria-label="$t('CONTACT_FORM.FORM.SUBMIT')"
          >
            <span
              v-if="uiFlags.isUpdating"
              class="i-lucide-loader-circle contact-edit-drawer__submit-loader"
            />
            <span v-else class="i-lucide-check" />
          </button>
        </form>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.contact-edit-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2147483647;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid rgb(var(--border-weak));
  background: #ffffff;
  box-shadow: none;
}

.contact-edit-drawer__header {
  display: flex;
  height: 64px;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  background: #ffffff;
}

.contact-edit-drawer__title {
  min-width: 0;
  flex: 1 1 auto;
  margin: 0;
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-edit-drawer__icon-button,
.contact-edit-drawer__header-spacer {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: rgb(var(--slate-12));
}

.contact-edit-drawer__icon-button {
  cursor: pointer;
  transition: background 160ms ease;
}

.contact-edit-drawer__icon-button:hover,
.contact-edit-drawer__icon-button:focus-visible {
  background: rgba(194, 189, 184, 0.18);
  outline: none;
}

.contact-edit-drawer__icon-button span {
  width: 20px;
  height: 20px;
  font-size: 24px;
}

.contact-edit-drawer__icon-button--delete {
  justify-self: end;
}

.contact-edit-drawer__form {
  position: relative;
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  padding: 64px 32px 112px;
}

.contact-edit-drawer__field-row,
.contact-edit-drawer__phone-row {
  display: grid;
  width: 100%;
  align-items: end;
  column-gap: 18px;
}

.contact-edit-drawer__field-row {
  grid-template-columns: 22px minmax(0, 1fr);
  margin-top: 0;
}

.contact-edit-drawer__field-row--no-icon {
  grid-template-columns: 22px minmax(0, 1fr);
  margin-top: 88px;
}

.contact-edit-drawer__field-row--no-icon::before {
  content: '';
}

.contact-edit-drawer__phone-row {
  grid-template-columns: 22px minmax(140px, 160px) minmax(0, 1fr) 24px;
  margin-top: 88px;
}

.contact-edit-drawer__field-icon {
  width: 18px;
  height: 18px;
  margin-bottom: 13px;
  color: #5f6368;
  font-size: 18px;
}

.contact-edit-drawer__field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  color: #5f6368;
}

.contact-edit-drawer__field--country {
  min-width: 0;
}

.contact-edit-drawer__label {
  color: #80868b;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.25;
}

.contact-edit-drawer__input,
.contact-edit-drawer__select {
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  margin: 0;
  padding: 0;
  border: 0;
  border-bottom: 3px solid #9aa0a6;
  border-radius: 0;
  background: transparent;
  color: #202124;
  font-size: 24px;
  font-weight: 400;
  line-height: 42px;
  outline: none;
  box-shadow: none;
}

.contact-edit-drawer__input:focus,
.contact-edit-drawer__select:focus {
  border-bottom-color: #20b576;
  box-shadow: none;
}

.contact-edit-drawer__select {
  appearance: auto;
  cursor: pointer;
}

.contact-edit-drawer__phone-valid {
  width: 18px;
  height: 18px;
  margin-bottom: 12px;
  color: #20b576;
  font-size: 24px;
}

.contact-edit-drawer__phone-helper {
  margin: 8px 0 0 40px;
  color: #5f6368;
  font-size: 18px;
  line-height: 1.4;
}

.contact-edit-drawer__phone-helper--error {
  color: #b42318;
}

.contact-edit-drawer__submit {
  position: absolute;
  left: 50%;
  bottom: 40px;
  display: inline-flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  transform: translateX(-50%);
  border: 0;
  border-radius: 9999px;
  background: #20b576;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 168, 132, 0.28);
  transition:
    background 160ms ease,
    transform 160ms ease;
}

.contact-edit-drawer__submit:hover,
.contact-edit-drawer__submit:focus-visible {
  background: #169c63;
  outline: none;
}

.contact-edit-drawer__submit:active {
  transform: translateX(-50%) scale(0.96);
}

.contact-edit-drawer__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.contact-edit-drawer__submit span {
  width: 28px;
  height: 28px;
  font-size: 28px;
}

.contact-edit-drawer__submit-loader {
  animation: contact-edit-spin 900ms linear infinite;
}

@media (max-width: 767px) {
  .contact-edit-drawer__header {
    padding: 0 12px;
  }

  .contact-edit-drawer__form {
    padding: 32px 20px 96px;
  }

  .contact-edit-drawer__field-row--no-icon,
  .contact-edit-drawer__phone-row {
    margin-top: 48px;
  }

  .contact-edit-drawer__phone-row {
    grid-template-columns: 22px minmax(104px, 0.45fr) minmax(0, 1fr) 24px;
    column-gap: 12px;
  }

  .contact-edit-drawer__label {
    font-size: 14px;
  }

  .contact-edit-drawer__input,
  .contact-edit-drawer__select {
    font-size: 18px;
  }

  .contact-edit-drawer__phone-helper {
    margin-left: 34px;
    font-size: 14px;
  }
}

.contact-edit-drawer-enter-active,
.contact-edit-drawer-leave-active {
  transition: transform 220ms ease-in-out;
}

.contact-edit-drawer-enter-from,
.contact-edit-drawer-leave-to {
  transform: translateX(100%);
}

@keyframes contact-edit-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
